// Slot-keyed, so unlike `assignMaster.ts` it needs no CSE; callers still keep on-request work orders
// out, since a slot makes one addressable without making it belong on the weekly template.
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'
import { buildSlotSequencePairs } from '../slotPayloads'

import type { SlotRow } from '../slotPayloads'
import type { StepResult } from '../types'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type { DayOfWeek, RouteId, ServiceDate } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

const BATCH_SIZE = 200

export interface AssignMasterSlotStepArgs {
  readonly routeId: RouteId
  readonly dayOfWeek: DayOfWeek
  readonly rows: ReadonlyArray<SlotRow>
  readonly futureStops?: boolean
  readonly futureStart?: ServiceDate
  readonly startSequence?: number
}

export type AssignMasterSlotStepValue = {
  readonly stopCount: number
  readonly batchCount: number
}

export type AssignMasterSlotStepResult = StepResult<'ASSIGN_MASTER', AssignMasterSlotStepValue>

export const makeAssignMasterSlotStep = (
  args: AssignMasterSlotStepArgs,
  deps: Deps
): PipelineStep<'ASSIGN_MASTER', AssignMasterSlotStepResult> => {
  const stepId = `assign-master-slot:${args.routeId}:${args.dayOfWeek}`
  return {
    id: stepId,
    phase: 'ASSIGN_MASTER',
    label: 'Failed to assign stops to master sequence',
    applyOrder: 300,
    run: async ctx => {
      if (!deps.assignMasterSlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'assignMasterSlot dep not provided — the v3-slot strategy requires it' })
      }
      const { pairs, droppedDuplicates, droppedMissing } = buildSlotSequencePairs(args.rows, args.startSequence ?? 1)
      if (droppedDuplicates.length > 0 || droppedMissing > 0) {
        ctx.logger.warn(`${stepId}: dropped ${droppedDuplicates.length} duplicate and ${droppedMissing} slot-less rows`)
      }
      if (pairs.length === 0) {
        return ok({ phase: 'ASSIGN_MASTER', stepId, value: { stopCount: 0, batchCount: 0 } })
      }

      // Each batch lands at the head as a block, so the last one has to go first: sent in order, batch
      // two would sit above batch one and a selection past BATCH_SIZE would commit reversed.
      const batches = chunk(pairs, BATCH_SIZE).reverse()
      let stopCount = 0
      try {
        // eslint-disable-next-line no-restricted-syntax -- batches apply in order; an array method would fan them out
        for (const batch of batches) {
          // eslint-disable-next-line no-await-in-loop -- sequential by design, see above
          const response = await deps
            .assignMasterSlot({
              routeId: args.routeId,
              data: {
                dayOfWeek: args.dayOfWeek as ApiDayOfWeek,
                futureStops: args.futureStops ?? true,
                futureStart: args.futureStart ?? null,
                stops: [...batch],
              },
              skipInvalidate: true,
            })
            .unwrap()
          stopCount += response?.stopCount ?? 0
          ctx.recordCommit()
        }
        return ok({ phase: 'ASSIGN_MASTER', stepId, value: { stopCount, batchCount: batches.length } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'ASSIGN_MASTER', status, message })
      }
    },
  }
}
